import React, { useCallback, useEffect, useRef } from 'react';
import get from 'lodash/get';
import omit from 'lodash/omit';

import { IconsEnum } from '../../assets/icons/types';
import { ClassName, FetchItemsFilterItems, GADataType } from '../../types';

import { useTranslate } from '../../common/hooks/useTranslate';
import { usePreviousValue } from '../../common/hooks/usePreviousValue';

import { Icon } from '../Icon';

import { words } from '../../locales/keys';

const INNER_FIELD = 'ilike';

interface QuickSearchInputHelperProps<FiltersType> {
  className?: ClassName;
  currentFilters?: FiltersType;
  iconClassName?: ClassName;
  iconWrapperClassName?: ClassName;
  inputClassName?: ClassName;
  onFilter: FetchItemsFilterItems<Partial<FiltersType>>;
  searchField: keyof FiltersType;
}

function QuickSearchInputHelper<FiltersType extends Record<string, any>>({
  className,
  currentFilters,
  dataGa,
  iconClassName,
  iconWrapperClassName,
  inputClassName,
  onFilter,
  searchField
}: QuickSearchInputHelperProps<FiltersType> & GADataType) {
  const { t } = useTranslate();

  const inputRef = useRef<HTMLInputElement>(null);

  const previousIsFiltered = usePreviousValue(currentFilters[searchField]);

  useEffect(() => {
    if (
      !currentFilters[searchField] &&
      previousIsFiltered &&
      inputRef.current.value
    ) {
      inputRef.current.value = '';
    }
  }, [currentFilters, previousIsFiltered, searchField]);

  const handleApply = useCallback<(value?: string) => void>(
    (value) => {
      if (value) {
        onFilter({
          ...currentFilters,
          [searchField]: {
            [INNER_FIELD]: value
          }
        });
      } else {
        onFilter(omit<FiltersType>(currentFilters, [searchField]));
      }
    },
    [currentFilters, searchField, onFilter]
  );

  const handleBlur = useCallback<React.FocusEventHandler<HTMLInputElement>>(
    (e) => {
      handleApply(e.target?.value);
    },
    [handleApply]
  );

  const handleEnterPress = useCallback<
    React.KeyboardEventHandler<HTMLInputElement>
  >(
    (e) => {
      if (e.code === 'Enter' || e.code === 'NumpadEnter') {
        handleApply((e.target as HTMLInputElement)?.value);
      }
    },
    [handleApply]
  );

  return (
    <div className={className || 'group relative flex h-full flex-1 min-w-20'}>
      <input
        ref={inputRef}
        data-ga={`${dataGa}-quick-search-input`}
        defaultValue={
          get(currentFilters, [searchField, INNER_FIELD].join('.')) || ''
        }
        onBlur={handleBlur}
        onKeyDown={handleEnterPress}
        placeholder={t(words.search)}
        className={
          inputClassName ||
          'bg-transparent font-normal border-none focus:bg-gray dark:focus:bg-gray-700 text-xs focus:ring-0 w-full p-0 py-2 px-2 pl-6 if not data.noSearchIcon hover:shadow dark:hover:glow'
        }
        type="text"
      />
      <div
        className={
          iconWrapperClassName ||
          'flex items-center absolute left-1 top-0 h-full pointer-events-none text-gray-500'
        }
      >
        <Icon className={iconClassName || 'h-4 w-4'} icon={IconsEnum.SEARCH} />
      </div>
    </div>
  );
}

export default QuickSearchInputHelper;
